import { allocateByWeights, allocateEvenly, sum } from './money';
import type { SplitType } from './types';

/**
 * One row of the split editor. What `value` means depends on the split type:
 *
 * | type       | value                                             |
 * |------------|---------------------------------------------------|
 * | equal      | ignored                                           |
 * | exact      | minor units this member owes (required)           |
 * | percentage | percent of the total, 0–100                       |
 * | shares     | relative weight, e.g. 2 for a couple              |
 * | custom     | minor units, or `null` to take an even cut of the |
 * |            | leftover after the fixed amounts are taken out    |
 */
export type ParticipantInput = {
  memberId: string;
  value: number | null;
};

export type ComputedShare = {
  memberId: string;
  shareAmountMinor: number;
  /** Raw input worth persisting so the edit screen can restore it verbatim. */
  weight: number | null;
};

/** Tolerance for percentage sums, in percentage points. */
const PERCENT_EPSILON = 0.001;

/**
 * Turns the split editor's state into per-member amounts.
 *
 * Always returns one entry per participant and, whenever the input is valid,
 * the entries sum to exactly `totalMinor` — remainders are distributed with
 * the largest-remainder method rather than dropped. Invalid input still
 * produces a best-effort result so the UI can show a live preview next to the
 * message from `validateSplit`.
 */
export function computeSplit(
  splitType: SplitType,
  totalMinor: number,
  participants: ParticipantInput[]
): ComputedShare[] {
  if (participants.length === 0) return [];

  switch (splitType) {
    case 'equal': {
      const amounts = allocateEvenly(totalMinor, participants.length);
      return participants.map((participant, index) => ({
        memberId: participant.memberId,
        shareAmountMinor: amounts[index],
        weight: null,
      }));
    }

    case 'exact': {
      return participants.map((participant) => ({
        memberId: participant.memberId,
        shareAmountMinor: Math.round(participant.value ?? 0),
        weight: null,
      }));
    }

    case 'percentage': {
      const percentages = participants.map((participant) => participant.value ?? 0);
      const amounts = allocateByWeights(totalMinor, percentages);
      return participants.map((participant, index) => ({
        memberId: participant.memberId,
        shareAmountMinor: amounts[index],
        weight: percentages[index],
      }));
    }

    case 'shares': {
      const shares = participants.map((participant) => participant.value ?? 0);
      const amounts = allocateByWeights(totalMinor, shares);
      return participants.map((participant, index) => ({
        memberId: participant.memberId,
        shareAmountMinor: amounts[index],
        weight: shares[index],
      }));
    }

    case 'custom': {
      const fixedTotal = sum(
        participants.map((participant) => Math.round(participant.value ?? 0))
      );
      const openIndexes = participants
        .map((participant, index) => (participant.value === null ? index : -1))
        .filter((index) => index >= 0);

      const leftover = allocateEvenly(totalMinor - fixedTotal, openIndexes.length);
      const byIndex = new Map(openIndexes.map((index, slot) => [index, leftover[slot]]));

      return participants.map((participant, index) => ({
        memberId: participant.memberId,
        shareAmountMinor:
          participant.value === null
            ? (byIndex.get(index) ?? 0)
            : Math.round(participant.value),
        weight: null,
      }));
    }
  }
}

/**
 * Returns a human-readable problem with the split, or `null` when it is
 * saveable. `formatAmount` renders minor units for the error message so the
 * caller controls currency.
 */
export function validateSplit(
  splitType: SplitType,
  totalMinor: number,
  participants: ParticipantInput[],
  formatAmount: (minor: number) => string
): string | null {
  if (participants.length === 0) {
    return 'Select at least one participant.';
  }
  if (totalMinor <= 0) {
    return 'Enter an amount greater than zero.';
  }

  switch (splitType) {
    case 'equal':
      return null;

    case 'exact': {
      const values = participants.map((participant) => participant.value);
      if (values.some((value) => value === null)) {
        return 'Enter an amount for every participant.';
      }
      if (values.some((value) => (value ?? 0) < 0)) {
        return 'Amounts cannot be negative.';
      }
      const total = sum(values.map((value) => value ?? 0));
      if (total !== totalMinor) {
        const difference = totalMinor - total;
        return `Shares add up to ${formatAmount(total)} — ${formatAmount(
          Math.abs(difference)
        )} ${difference > 0 ? 'short of' : 'over'} the ${formatAmount(totalMinor)} total.`;
      }
      return null;
    }

    case 'percentage': {
      const percentages = participants.map((participant) => participant.value ?? 0);
      if (percentages.some((value) => value < 0)) {
        return 'Percentages cannot be negative.';
      }
      const total = sum(percentages);
      if (Math.abs(total - 100) > PERCENT_EPSILON) {
        return `Percentages add up to ${formatPercent(total)}% — they must total 100%.`;
      }
      return null;
    }

    case 'shares': {
      const shares = participants.map((participant) => participant.value ?? 0);
      if (shares.some((value) => value < 0)) {
        return 'Shares cannot be negative.';
      }
      if (sum(shares) <= 0) {
        return 'Give at least one participant a share above zero.';
      }
      return null;
    }

    case 'custom': {
      const fixed = participants.filter((participant) => participant.value !== null);
      if (fixed.some((participant) => (participant.value ?? 0) < 0)) {
        return 'Amounts cannot be negative.';
      }

      const fixedTotal = sum(fixed.map((participant) => participant.value ?? 0));
      const openCount = participants.length - fixed.length;

      if (openCount === 0) {
        if (fixedTotal !== totalMinor) {
          const difference = totalMinor - fixedTotal;
          return `Shares add up to ${formatAmount(fixedTotal)} — ${formatAmount(
            Math.abs(difference)
          )} ${difference > 0 ? 'short of' : 'over'} the ${formatAmount(totalMinor)} total.`;
        }
        return null;
      }

      if (fixedTotal > totalMinor) {
        return `Fixed amounts (${formatAmount(
          fixedTotal
        )}) already exceed the ${formatAmount(totalMinor)} total.`;
      }
      return null;
    }
  }
}

function formatPercent(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
}

/**
 * Seeds the split editor when the user switches split type, so the new mode
 * starts from something sensible instead of a blank form.
 */
export function defaultValuesForSplitType(
  splitType: SplitType,
  totalMinor: number,
  memberIds: string[]
): ParticipantInput[] {
  switch (splitType) {
    case 'equal':
      return memberIds.map((memberId) => ({ memberId, value: null }));

    case 'exact': {
      const amounts = allocateEvenly(totalMinor, memberIds.length);
      return memberIds.map((memberId, index) => ({ memberId, value: amounts[index] }));
    }

    case 'percentage': {
      const even = memberIds.length > 0 ? 100 / memberIds.length : 0;
      const rounded = Math.floor(even * 100) / 100;
      // Give the rounding slack to the first member so the column totals 100%.
      const drift = Math.round((100 - rounded * memberIds.length) * 100) / 100;
      return memberIds.map((memberId, index) => ({
        memberId,
        value: index === 0 ? Math.round((rounded + drift) * 100) / 100 : rounded,
      }));
    }

    case 'shares':
      return memberIds.map((memberId) => ({ memberId, value: 1 }));

    case 'custom':
      return memberIds.map((memberId) => ({ memberId, value: null }));
  }
}
